/**
 * User-friendly error message mapping
 * Converts technical error codes to human-readable messages with actionable suggestions
 */

export interface ErrorInfo {
  title: string;
  message: string;
  suggestion?: string;
  helpUrl?: string;
}

// Error code prefixes
export const ErrorPrefix = {
  Validation: 'VAL',
  Migration: 'MIG',
  Auth: 'AUTH',
  Network: 'NET',
  Server: 'SRV',
  Module: 'MOD',
} as const;

/**
 * Error code to user-friendly message mapping
 */
const errorMessages: Record<string, ErrorInfo> = {
  // Validation Errors
  VAL001: {
    title: 'Invalid Organization UUID',
    message: 'The organization identifier provided is not in a valid format.',
    suggestion: 'Please ensure the UUID follows the format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
  },
  VAL002: {
    title: 'Organization Not Found',
    message: 'No organization was found with the provided identifier.',
    suggestion: 'Please verify the organization UUID is correct and exists in the source system.',
  },
  VAL003: {
    title: 'Module Not Available',
    message: 'The selected module is not available for migration.',
    suggestion: 'Please contact your administrator to enable this module.',
  },
  VAL004: {
    title: 'Validation Failed',
    message: 'One or more validation checks failed for the selected modules.',
    suggestion: 'Review the detailed error messages below and resolve any data issues.',
  },
  VAL005: {
    title: 'Required Field Missing',
    message: 'A required field was not provided.',
    suggestion: 'Please fill in all required fields before proceeding.',
  },
  VAL006: {
    title: 'Data Format Error',
    message: 'The provided data is not in the expected format.',
    suggestion: 'Please check the data format requirements and try again.',
  },

  // Migration Errors
  MIG001: {
    title: 'Migration Already In Progress',
    message: 'A migration is already running for this organization.',
    suggestion: 'Please wait for the current migration to complete or cancel it before starting a new one.',
  },
  MIG002: {
    title: 'Migration Failed',
    message: 'The migration encountered an error and could not complete.',
    suggestion: 'Review the error details and retry the failed modules, or contact support.',
  },
  MIG003: {
    title: 'Module Migration Failed',
    message: 'One or more modules failed to migrate successfully.',
    suggestion: 'You can retry individual failed modules using the retry button.',
  },
  MIG004: {
    title: 'Migration Timeout',
    message: 'The migration took longer than expected and timed out.',
    suggestion: 'This may be due to large data volumes. Try again or contact support.',
  },
  MIG005: {
    title: 'Migration Cancelled',
    message: 'The migration was cancelled by a user.',
    suggestion: 'You can start a new migration when ready.',
  },
  MIG006: {
    title: 'Retry Limit Exceeded',
    message: 'The maximum number of retry attempts has been reached.',
    suggestion: 'Please contact support for assistance with this migration.',
    helpUrl: '/help/retry-limits',
  },

  // Authentication Errors
  AUTH001: {
    title: 'Authentication Required',
    message: 'You need to sign in to access this feature.',
    suggestion: 'Please sign in with your organization account.',
  },
  AUTH002: {
    title: 'Session Expired',
    message: 'Your session has expired.',
    suggestion: 'Please sign in again to continue.',
  },
  AUTH003: {
    title: 'Access Denied',
    message: 'You do not have permission to perform this action.',
    suggestion: 'Contact your administrator if you believe you should have access.',
  },
  AUTH004: {
    title: 'Insufficient Permissions',
    message: 'Your role does not allow this operation.',
    suggestion: 'Contact an administrator to request elevated permissions.',
  },

  // Network Errors
  NET001: {
    title: 'Connection Error',
    message: 'Unable to connect to the server.',
    suggestion: 'Please check your internet connection and try again.',
  },
  NET002: {
    title: 'Request Timeout',
    message: 'The server took too long to respond.',
    suggestion: 'Please try again. If the issue persists, contact support.',
  },
  NET003: {
    title: 'Service Unavailable',
    message: 'The service is temporarily unavailable.',
    suggestion: 'Please wait a few minutes and try again.',
  },

  // Server Errors
  SRV001: {
    title: 'Server Error',
    message: 'An unexpected error occurred on the server.',
    suggestion: 'Please try again later. If the issue persists, contact support.',
  },
  SRV002: {
    title: 'Database Error',
    message: 'A database error occurred while processing your request.',
    suggestion: 'Please try again. If the issue persists, contact support.',
  },

  // Module-Specific Errors
  MOD_GIVING_001: {
    title: 'Giving Data Error',
    message: 'Unable to access giving transaction data.',
    suggestion: 'Verify the organization has giving data and the API connection is active.',
  },
  MOD_GIVING_002: {
    title: 'Donor Records Missing',
    message: 'No donor records were found for migration.',
    suggestion: 'Ensure the source system contains donor records before migrating.',
  },
  MOD_PEOPLE_001: {
    title: 'People Data Error',
    message: 'Unable to access people/member data.',
    suggestion: 'Verify the organization has people records and the API connection is active.',
  },
  MOD_PEOPLE_002: {
    title: 'Duplicate Records Detected',
    message: 'Duplicate member records were found that may cause conflicts.',
    suggestion: 'Review and deduplicate records in the source system before migrating.',
  },
  MOD_PLANNING_001: {
    title: 'Service Planning Data Error',
    message: 'Unable to access service planning data.',
    suggestion: 'Verify the organization uses service planning and the API connection is active.',
  },
};

/**
 * Get user-friendly error information from an error code
 */
export function getErrorInfo(errorCode: string): ErrorInfo {
  const info = errorMessages[errorCode];

  if (info) {
    return info;
  }

  // Return generic error for unknown codes
  return {
    title: 'Error',
    message: `An error occurred (${errorCode}).`,
    suggestion: 'Please try again. If the issue persists, contact support.',
  };
}

/**
 * Parse error response from API and return user-friendly error info
 */
export function parseApiError(error: unknown): ErrorInfo {
  // Handle axios/fetch error responses
  if (typeof error === 'object' && error !== null) {
    const err = error as Record<string, unknown>;

    // Check for error code in response
    if (err.errorCode && typeof err.errorCode === 'string') {
      return getErrorInfo(err.errorCode);
    }

    // Check for error code in response data
    if (err.response && typeof err.response === 'object') {
      const response = err.response as Record<string, unknown>;
      if (response.data && typeof response.data === 'object') {
        const data = response.data as Record<string, unknown>;
        if (data.errorCode && typeof data.errorCode === 'string') {
          return getErrorInfo(data.errorCode);
        }
        if (data.message && typeof data.message === 'string') {
          return {
            title: 'Error',
            message: data.message,
          };
        }
      }
    }

    // Check for message property
    if (err.message && typeof err.message === 'string') {
      return parseErrorMessage(err.message);
    }
  }

  // Handle string errors
  if (typeof error === 'string') {
    return parseErrorMessage(error);
  }

  // Default error
  return {
    title: 'Unexpected Error',
    message: 'An unexpected error occurred.',
    suggestion: 'Please try again. If the issue persists, contact support.',
  };
}

/**
 * Parse common error message patterns
 */
function parseErrorMessage(message: string): ErrorInfo {
  const lowerMessage = message.toLowerCase();

  if (lowerMessage.includes('network') || lowerMessage.includes('fetch') || lowerMessage.includes('connect')) {
    return getErrorInfo('NET001');
  }

  if (lowerMessage.includes('timeout')) {
    return getErrorInfo('NET002');
  }

  if (lowerMessage.includes('unauthorized') || lowerMessage.includes('401')) {
    return getErrorInfo('AUTH001');
  }

  if (lowerMessage.includes('forbidden') || lowerMessage.includes('403')) {
    return getErrorInfo('AUTH003');
  }

  if (lowerMessage.includes('not found') || lowerMessage.includes('404')) {
    return {
      title: 'Not Found',
      message: 'The requested resource was not found.',
      suggestion: 'Please check the URL and try again.',
    };
  }

  if (lowerMessage.includes('500') || lowerMessage.includes('server error')) {
    return getErrorInfo('SRV001');
  }

  return {
    title: 'Error',
    message,
    suggestion: 'Please try again. If the issue persists, contact support.',
  };
}

/**
 * Format error for display in toast/alert
 */
export function formatErrorForToast(error: unknown): { title: string; description: string } {
  const info = parseApiError(error);
  return {
    title: info.title,
    description: info.suggestion ? `${info.message} ${info.suggestion}` : info.message,
  };
}

/**
 * Error codes for validation
 */
export const ValidationErrorCodes = {
  InvalidUuid: 'VAL001',
  OrganizationNotFound: 'VAL002',
  ModuleNotAvailable: 'VAL003',
  ValidationFailed: 'VAL004',
  RequiredFieldMissing: 'VAL005',
  DataFormatError: 'VAL006',
} as const;

/**
 * Error codes for migration
 */
export const MigrationErrorCodes = {
  AlreadyInProgress: 'MIG001',
  Failed: 'MIG002',
  ModuleFailed: 'MIG003',
  Timeout: 'MIG004',
  Cancelled: 'MIG005',
  RetryLimitExceeded: 'MIG006',
} as const;
